import { useState } from "react";
import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { wingDetails } from "@/mocks/programsData";

const itemRoutes: Record<string, string> = {
  PIIC: "/piic",
  PTIM: "/ptim",
  Training: "/training",
  Experts: "/experts",
  PIARC: "/piarc",
  IJTAI: "/ijtai",
  AIRCon: "/aircon",
};

const wingRoutes: Record<string, string> = {
  "Innovation Wing": "/wings/innovation",
  "Research Wing": "/wings/research",
  "Readiness & Recognition": "/wings/readiness",
  "Expo Wing": "/wings/expo",
};

export default function WingsSection() {
  const [activeWing, setActiveWing] = useState(wingDetails[0].wing);

  const activeData = wingDetails.find((w) => w.wing === activeWing);

  return (
    <section id="wings" className="bg-white py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-5">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / The Four Wings
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl font-bold text-tie-text-primary leading-tight">
                From Factory Floor to Global Market
              </h2>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
                Each wing addresses a distinct phase of the innovation
                lifecycle. Together, they form an operating system that no
                single programme, consultant, or institution can replicate.
              </p>
            </div>
          </ScrollReveal>

          {/* Wing Tabs */}
          <ScrollReveal delay={100}>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12">
              {wingDetails.map((wing) => {
                const wingRoute = wingRoutes[wing.wing];
                return (
                  <button
                    key={wing.wing}
                    onClick={() => setActiveWing(wing.wing)}
                    className={`inline-flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeWing === wing.wing
                        ? "bg-tie-navy text-white"
                        : "bg-tie-grey text-tie-text-secondary hover:bg-tie-grey-dark"
                    }`}
                  >
                    <i className={wing.icon} />
                    {wing.wing}
                  </button>
                );
              })}
            </div>
          </ScrollReveal>

          {/* Wing description with link */}
          <ScrollReveal delay={150}>
            <div className="text-center max-w-3xl mx-auto mb-8 md:mb-10">
              <p className="text-sm md:text-base text-tie-text-secondary leading-relaxed">
                <span className="font-semibold text-tie-text-primary">
                  {activeData?.tagline}
                </span>{" "}
                {activeData?.description}
              </p>
              {activeData && wingRoutes[activeData.wing] && (
                <Link
                  to={wingRoutes[activeData.wing]}
                  className="inline-flex items-center mt-3 text-sm font-medium text-tie-teal hover:text-tie-teal-dark transition-colors"
                >
                  Explore {activeData.wing}
                  <i className="ri-arrow-right-line ml-1.5 text-xs" />
                </Link>
              )}
            </div>
          </ScrollReveal>

          {/* Programme Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {activeData?.items.map((item, index) => {
              const route = itemRoutes[item.code];
              const CardContent = (
                <div className="group h-full p-5 md:p-6 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg transition-all duration-300">
                  {/* Top row */}
                  <div className="flex items-start justify-between mb-3 md:mb-4">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-9 h-9 rounded-lg ${activeData?.iconBg} flex items-center justify-center`}
                      >
                        <i className={`${activeData?.icon} text-white text-sm`} />
                      </div>
                      <span className="text-xs font-bold text-tie-teal uppercase tracking-wider">
                        {item.code}
                      </span>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-tie-grey-dark">
                      <i className="ri-arrow-right-line text-tie-teal text-sm" />
                    </div>
                  </div>

                  <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-1">
                    {item.name}
                  </h3>
                  <p className="text-xs md:text-sm text-tie-text-muted mb-3 md:mb-4">
                    {item.tagline}
                  </p>
                  <p className="text-sm text-tie-text-secondary leading-relaxed mb-4 md:mb-5">
                    {item.detail}
                  </p>

                  {/* Outcomes */}
                  <div className="space-y-2">
                    {item.outcomes.map((outcome) => (
                      <div
                        key={outcome}
                        className="flex items-center gap-2.5 text-sm text-tie-text-secondary"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-tie-teal shrink-0" />
                        {outcome}
                      </div>
                    ))}
                  </div>

                  {route && (
                    <div className="mt-4 pt-4 border-t border-tie-grey-dark">
                      <span className="inline-flex items-center text-sm font-medium text-tie-teal group-hover:text-tie-teal-dark transition-colors">
                        Learn more
                        <i className="ri-arrow-right-line ml-1.5 text-xs group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  )}
                </div>
              );

              return (
                <ScrollReveal key={item.code} delay={index * 100}>
                  {route ? (
                    <Link to={route} className="block cursor-pointer">
                      {CardContent}
                    </Link>
                  ) : (
                    CardContent
                  )}
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}